import React, { useEffect, useState } from "react";
import { api, rupiah, formatDate, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import StatusBadge from "../../components/StatusBadge";
import { toast } from "sonner";
import { Search, Settings2, Trash2, Building2, Ban, CheckCircle2 } from "lucide-react";

export default function AdminTenants() {
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState("");
  const [manage, setManage] = useState(null);
  const [plan, setPlan] = useState("basic");
  const [extend, setExtend] = useState("30");
  const [del, setDel] = useState(null);

  const load = () => api.get("/admin/tenants", { params: { search } }).then((r) => setTenants(r.data)).catch(() => {});
  useEffect(() => { load(); }, [search]);

  const openManage = (t) => { setManage(t); setPlan(t.subscription.plan); setExtend("30"); };

  const saveSub = async () => {
    try {
      await api.put(`/admin/tenants/${manage.id}/subscription`, { plan, extend_days: Number(extend) || 0 });
      toast.success("Langganan diperbarui"); setManage(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleSuspend = async (t) => {
    try {
      await api.put(`/admin/tenants/${t.id}/subscription`, { suspended: !t.suspended });
      toast.success(t.suspended ? "Toko diaktifkan" : "Toko ditangguhkan"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/admin/tenants/${del.id}`); toast.success("Toko dihapus"); setDel(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-tenants-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Toko Pelanggan</h1>
        <p className="text-muted-foreground text-sm">Kelola seluruh toko dan langganannya.</p>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama toko..." className="pl-9 rounded-lg" data-testid="tenant-search" />
      </div>

      <Card className="border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto kasir-scroll">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-muted-foreground">
              <tr className="text-left">
                <th className="py-3 px-4 font-medium">Toko</th>
                <th className="py-3 px-4 font-medium">Pemilik</th>
                <th className="py-3 px-4 font-medium">Paket</th>
                <th className="py-3 px-4 font-medium">Status</th>
                <th className="py-3 px-4 font-medium">Berlaku Hingga</th>
                <th className="py-3 px-4 font-medium text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id} className={`border-t border-slate-100 hover:bg-slate-50 ${t.suspended ? "opacity-60" : ""}`} data-testid={`tenant-row-${t.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-accent text-primary flex items-center justify-center"><Building2 className="w-4 h-4" /></div>
                      <div>
                        <div className="font-medium">{t.name}</div>
                        <div className="text-xs text-muted-foreground">{t.product_count} produk • {t.transaction_count} transaksi</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div>{t.owner_name}</div>
                    <div className="text-xs text-muted-foreground">{t.owner_email}</div>
                  </td>
                  <td className="py-3 px-4 font-medium">{t.subscription.plan_name}</td>
                  <td className="py-3 px-4"><StatusBadge status={t.subscription.status} testid={`tenant-status-${t.id}`} /></td>
                  <td className="py-3 px-4 text-muted-foreground">{t.subscription.subscription_end ? formatDate(t.subscription.subscription_end) : "-"}</td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <button onClick={() => openManage(t)} className="text-slate-400 hover:text-primary p-1" title="Kelola langganan" data-testid={`manage-tenant-${t.id}`}><Settings2 className="w-4 h-4" /></button>
                    <button onClick={() => toggleSuspend(t)} className="text-slate-400 hover:text-amber-600 p-1" title={t.suspended ? "Aktifkan" : "Tangguhkan"} data-testid={`suspend-tenant-${t.id}`}>
                      {t.suspended ? <CheckCircle2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                    </button>
                    <button onClick={() => setDel(t)} className="text-slate-400 hover:text-red-600 p-1" title="Hapus" data-testid={`delete-tenant-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {tenants.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-muted-foreground">Belum ada toko terdaftar.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!manage} onOpenChange={() => setManage(null)}>
        <DialogContent data-testid="manage-sub-dialog">
          <DialogHeader><DialogTitle className="font-heading">Kelola Langganan — {manage?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Paket</Label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger className="mt-1 rounded-lg" data-testid="sub-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Perpanjang (hari)</Label>
              <Input type="number" value={extend} onChange={(e) => setExtend(e.target.value)} className="mt-1 rounded-lg" data-testid="sub-extend-input" />
              <p className="text-xs text-muted-foreground mt-1">Menambah masa aktif dari tanggal berlaku saat ini.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManage(null)} className="rounded-lg">Batal</Button>
            <Button onClick={saveSub} className="rounded-lg" data-testid="save-sub-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={() => setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus toko?</AlertDialogTitle>
            <AlertDialogDescription>Semua data toko "{del?.name}" (produk, transaksi, pengguna) akan dihapus permanen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="rounded-lg bg-red-600 hover:bg-red-700" data-testid="confirm-delete-tenant">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
